import { useCallback, useEffect, useRef, useState } from 'react';
import { Pause, Play, Volume2, VolumeX } from 'lucide-react';
import VideoMaximize from '../../../ui/VideoMaximize';

const REDUCE = '(prefers-reduced-motion: reduce)';
const prefersStill = () => typeof window.matchMedia === 'function' && window.matchMedia(REDUCE).matches;

// sai's AutoVideo: a showcase video that plays by itself, muted and looping,
// while it is on screen (browsers only allow autoplay without sound), and
// pauses when it scrolls away. Nothing loads until it is near the screen. Two
// small buttons, always there: play / pause, and sound on / off. Once paused by
// hand it stays paused when scrolled back to. Under reduced motion it never
// starts by itself, and it pauses if the visitor turns reduced motion on.
// A maximize button (src/ui/VideoMaximize.jsx) sits in the frame's top-right
// corner, clear of the two buttons at the bottom right.
//   video   — { src, poster, title } (see VIDEOS in DemoVideo.js)
//   soundId / playId — data-trapezium-ids for the two buttons
//   maximizeId — optional; by default playId with "play" in its last part
//                swapped for "maximize" (…showcase-play → …showcase-maximize,
//                …play-demo → …maximize-demo)
//   rate    — optional playback speed (default 1)
export const maximizeIdFor = (playId) => playId.replace(/\bplay\b(?=[^.]*$)/, 'maximize');

export default function AutoVideo({ video, soundId, playId, maximizeId = maximizeIdFor(playId), rate = 1 }) {
  const ref = useRef(null);
  // The element itself, for the maximize button (it needs it after mount).
  const [videoEl, setVideoEl] = useState(null);
  const setVideo = useCallback((el) => {
    ref.current = el;
    setVideoEl(el);
  }, []);
  const [muted, setMuted] = useState(true);
  const [still, setStill] = useState(prefersStill);
  const [playing, setPlaying] = useState(false);
  const held = useRef(false); // paused by the visitor: do not restart on scroll

  // Loading a source resets the speed to defaultPlaybackRate, so set both.
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    v.defaultPlaybackRate = rate;
    v.playbackRate = rate;
  }, [rate]);

  // Follow the reduced-motion setting while the page is open.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(REDUCE);
    if (!mq.addEventListener) return undefined;
    const onChange = (e) => {
      setStill(e.matches);
      if (e.matches && ref.current && !ref.current.paused) ref.current.pause();
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const v = ref.current;
    if (!v || still || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          if (held.current) return;
          if (!v.src) v.src = video.src; // load on first view
          v.play().catch(() => {});
        } else if (!v.paused) v.pause();
      },
      { threshold: 0.35 },
    );
    io.observe(v);
    return () => io.disconnect();
  }, [still, video.src]);

  const start = () => {
    const v = ref.current;
    held.current = false;
    if (!v.src) v.src = video.src;
    v.play().catch(() => {});
  };
  const togglePlay = () => {
    if (ref.current.paused) start();
    else {
      held.current = true;
      ref.current.pause();
    }
  };

  return (
    <div className="avid" data-trapezium-view={`${playing ? 'playing' : 'paused'} ${muted ? 'muted' : 'sound'}`}>
      <video
        ref={setVideo}
        poster={video.poster}
        data-src={video.src}
        muted={muted}
        loop
        playsInline
        preload="none"
        onLoadedMetadata={(e) => {
          e.currentTarget.playbackRate = rate;
        }}
        onPlay={(e) => {
          e.currentTarget.playbackRate = rate; // some browsers reset it on a new source
          setPlaying(true);
        }}
        onPause={() => setPlaying(false)}
        aria-label={video.title}
      />
      <div className="avid-ctrl">
        <button
          type="button"
          className="avid-play"
          onClick={togglePlay}
          data-trapezium-id={playId}
          aria-label={`${playing ? 'Pause' : 'Play'} the ${video.title}`}
        >
          {playing ? (
            <Pause size={15} fill="currentColor" aria-hidden="true" />
          ) : (
            <Play size={15} fill="currentColor" aria-hidden="true" />
          )}
        </button>
        <button
          type="button"
          className="avid-sound"
          onClick={() => {
            setMuted(!muted);
            if (ref.current && ref.current.paused) start();
          }}
          aria-pressed={!muted}
          aria-label={`${muted ? 'Sound on' : 'Sound off'} for the ${video.title}`}
          data-trapezium-id={soundId}
        >
          {muted ? <VolumeX size={15} aria-hidden="true" /> : <Volume2 size={15} aria-hidden="true" />}
          {muted ? 'Sound on' : 'Sound off'}
        </button>
      </div>
      <VideoMaximize video={videoEl} id={maximizeId} label={`the ${video.title}`} />
    </div>
  );
}
